import * as v from 'valibot';
import type { BaseIssue, BaseSchema } from 'valibot';
import { ResponseWithIdSchema, ExportReportSchema } from '~/schemas';

type CustomSchema = BaseSchema<unknown, unknown, BaseIssue<unknown>>;

export function assertSchema <TSchema extends CustomSchema> (value: unknown, schema: TSchema): asserts value is v.InferOutput<TSchema> {
	try {
		v.parse(schema, value);
	}
	catch (error) {
		throw new Error(`Invalid value: ${ error }`);
	}
}

export function assertResponseWithId (value: unknown): asserts value is v.InferOutput<typeof ResponseWithIdSchema> {
	assertSchema(value, ResponseWithIdSchema);
}

export function assertExportReportResponse (value: unknown): asserts value is v.InferOutput<typeof ExportReportSchema> {
	assertSchema(value, ExportReportSchema);
}

export const ApiErrorSchema = v.object({
	response: v.object({
		data: v.object({
			code: v.number(),
			message: v.string(),
		}),
	}),
});

export type ApiError = v.InferOutput<typeof ApiErrorSchema>;

export function assertApiError (value: unknown): asserts value is ApiError {
	assertSchema(value, ApiErrorSchema);
}

export const ApiNoRevisionErrorSchema = v.object({
	response: v.object({
		data: v.object({
			code: v.literal(404),
			message: v.string(),
			context: v.object({
				noRevision: v.object({
					campaignSplitId: v.number(),
				}),
			}),
		}),
	}),
});

export function isNoRevisionApiError (error: unknown): boolean {
	return v.safeParse(ApiNoRevisionErrorSchema, error).success;
}

export const ApiNotFailedErrorSchema = v.object({
	response: v.object({
		data: v.object({
			code: v.literal(409),
			message: v.string(),
			context: v.object({
				notFailed: v.object({
					revisionId: v.number(),
					state: v.picklist([ 'open', 'closed', 'applying', 'applied' ]),
				}),
			}),
		}),
	}),
});

export function isNotFailedApiError (error: unknown): boolean {
	return v.safeParse(ApiNotFailedErrorSchema, error).success;
}

export const ApiUnappliedRevisionErrorSchema = v.object({
	response: v.object({
		data: v.object({
			code: v.literal(409),
			message: v.string(),
			context: v.object({
				unappliedRevision: v.object({
					revisionId: v.number(),
					state: v.picklist([ 'closed', 'applying', 'failed' ]),
					version: v.nullable(v.number()),
				}),
			}),
		}),
	}),
});

export type UnappliedRevisionError = v.InferOutput<typeof ApiUnappliedRevisionErrorSchema>;

export function getUnappliedRevisionFromApiError (error: unknown): UnappliedRevisionError['response']['data']['context']['unappliedRevision'] | null {
	const result = v.safeParse(ApiUnappliedRevisionErrorSchema, error);
	return result.success ? result.output.response.data.context.unappliedRevision : null;
}

export function makeApiValidationErrorSchema <T extends CustomSchema> (contextSchema: T) {
	return v.object({
		response: v.object({
			data: v.object({
				code: v.literal(422),
				message: v.string(),
				context: contextSchema,
			}),
		}),
	});
}

export const ApiDefaultValidationContextSchema = makeApiValidationErrorSchema(v.object({
	validation: v.record(
		v.string(),
		v.array(v.string()),
	),
}));

export function assertApiDefaultValidationError (value: unknown): asserts value is v.InferOutput<typeof ApiDefaultValidationContextSchema> {
	assertSchema(value, ApiDefaultValidationContextSchema);
}

export const ApiCarrierInExcludedRegionErrorSchema = makeApiValidationErrorSchema(v.object({
	carrierInExcludedRegion: v.object({
		count: v.number(),
		reported: v.number(),
		carriers: v.array(v.object({
			productId: v.number(),
			productName: v.string(),
			posId: v.nullable(v.number()),
			posCode: v.string(),
			posName: v.string(),
			regionId: v.nullable(v.number()),
			regionName: v.string(),
		})),
	}),
}));

export type CarrierInExcludedRegion = v.InferOutput<typeof ApiCarrierInExcludedRegionErrorSchema>['response']['data']['context']['carrierInExcludedRegion'];

export function getCarrierInExcludedRegionFromApiError (error: unknown): CarrierInExcludedRegion | null {
	const result = v.safeParse(ApiCarrierInExcludedRegionErrorSchema, error);
	return result.success ? result.output.response.data.context.carrierInExcludedRegion : null;
}

export const ApiSpreadsheetValidationContextSchema = makeApiValidationErrorSchema(v.array(v.union([
	v.object({
		code: v.literal('promo.promo_check_not_found'),
		meta: v.object({
			category: v.string(),
			check: v.string(),
		}),
		row: v.number(),
	}),
	v.object({
		code: v.literal('promo.promo_first_offer_empty'),
		row: v.number(),
	}),
	v.object({
		code: v.literal('promo.priority.activity_not_found'),
		meta: v.object({
			activity: v.string(),
			colNames: v.array(v.string()),
		}),
		row: v.number(),
	}),
	v.object({
		code: v.literal('spreadsheet.missing_column'),
		meta: v.object({ column: v.string() }),
		row: v.null(),
	}),
	v.object({
		code: v.picklist([
			'validation.required',
			'validation.invalid_format',
		]),
		meta: v.record(v.string(), v.unknown()),
		row: v.nullable(v.number()),
	}),
])));

export function assertApiSpreadsheetValidationError (value: unknown): asserts value is v.InferOutput<typeof ApiSpreadsheetValidationContextSchema> {
	assertSchema(value, ApiSpreadsheetValidationContextSchema);
}
